<template>
  <div class="py-4 container-fluid">
    <!-- Nav Pills -->
    <div class="row mb-4">
      <div class="col-12">
        <ul class="nav nav-pills analytics-pills">
          <li class="nav-item">
            <button class="nav-link" :class="activeTab === 'overview' ? 'active' : ''" @click="onOverviewTab">
              <i class="material-icons-round align-middle" style="font-size:1.1rem">analytics</i>
              <span class="d-none d-sm-inline ms-1">{{ $t('analytics.overviewTab') }}</span>
            </button>
          </li>
          <li v-if="enableTimeRegistration" class="nav-item">
            <button class="nav-link" :class="activeTab === 'timeReports' ? 'active' : ''" @click="onTimeReportsTab">
              <i class="material-icons-round align-middle" style="font-size:1.1rem">schedule</i>
              <span class="d-none d-sm-inline ms-1">{{ $t('analytics.timeReportsTab') }}</span>
            </button>
          </li>
          <li v-for="tab in visibleReportTabs" :key="tab.key" class="nav-item">
            <button class="nav-link" :class="activeTab === tab.key ? 'active' : ''" @click="onReportTab(tab.key)">
              <i class="material-icons-round align-middle" style="font-size:1.1rem">{{ tab.icon }}</i>
              <span class="d-none d-sm-inline ms-1">{{ $t(tab.labelKey) }}</span>
            </button>
          </li>
        </ul>
      </div>
    </div>

    <!-- Report Tabs (SuperAdmin-enabled, rendered inline) -->
    <component :is="activeReportComponent" v-if="activeReportComponent" />

    <!-- Overview Tab -->
    <template v-if="activeTab === 'overview'">

    <!-- Stats Cards Row -->
    <div v-if="statusCardsLoaded" class="row">
      <div v-for="(card, index) in statsCards" :key="index" class="col-xl-3 col-sm-6 mb-4">
        <div class="card">
          <div class="p-3 card-body">
            <div class="row">
              <div class="col-8">
                <div class="numbers">
                  <p class="mb-0 text-sm text-uppercase font-weight-bold text-secondary">{{ card.subTitle }}</p>
                  <h5 class="mb-0 font-weight-bolder">{{ card.title }}</h5>
                </div>
              </div>
              <div class="col-4 text-end">
                <div class="icon icon-shape shadow-primary text-center border-radius-md" :class="getCardIconClass(card.type)">
                  <i class="material-icons opacity-10 stats-icon-fit" aria-hidden="true">{{ getCardIcon(card.type) }}</i>
                </div>
              </div>
            </div>
            <div class="mt-3 cursor-pointer" @click="goToRoute(card)">
              <span class="text-sm text-info font-weight-bold">
                <i class="material-icons text-sm align-middle">touch_app</i>
                {{ $t('analytics.reviewNow') || 'Review now' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Row 1: Projects & Users with Tools -->
    <div class="row mt-4">
      <!-- Projects with Tools Chart -->
      <div v-if="projectReservationBarChartLoaded" class="col-lg-6 col-12 mb-4">
        <div class="card z-index-2">
          <div class="card-header p-3 pt-2">
            <div class="icon icon-lg icon-shape bg-gradient-danger shadow-danger text-center border-radius-xl mt-n4 me-3 float-start">
              <i class="material-icons opacity-10 graph-icon-fit">list_alt</i>
            </div>
            <div class="d-block">
              <p class="text-sm mb-0 text-capitalize text-secondary">{{ $t('analytics.projectsWithTools') || 'Projects with Tools' }}</p>
              <h4 class="mb-0 font-weight-bolder">{{ projectWithToolCount }}</h4>
            </div>
          </div>
          <div class="p-3 card-body">
            <div class="chart">
              <canvas id="projects-bar-chart" class="chart-canvas" height="250"></canvas>
            </div>
          </div>
        </div>
      </div>

      <!-- Users with Tools Chart -->
      <div v-if="userReservationBarChartLoaded" class="col-lg-6 col-12 mb-4">
        <div class="card z-index-2">
          <div class="card-header p-3 pt-2">
            <div class="icon icon-lg icon-shape bg-gradient-info shadow-info text-center border-radius-xl mt-n4 me-3 float-start">
              <i class="material-icons opacity-10 graph-icon-fit">person</i>
            </div>
            <div class="d-block">
              <p class="text-sm mb-0 text-capitalize text-secondary">{{ $t('analytics.usersWithTools') || 'Users with Tools' }}</p>
              <h4 class="mb-0 font-weight-bolder">{{ usersWithReservationCount }}</h4>
            </div>
          </div>
          <div class="p-3 card-body">
            <div class="chart">
              <canvas id="users-bar-chart" class="chart-canvas" height="250"></canvas>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Row 2: Purchase Price & Tool Status -->
    <div class="row">
      <!-- Tool Purchase Price by Type Chart -->
      <div v-if="toolTypePurchasePricePieChartLoaded" class="col-lg-6 col-12 mb-4">
        <div class="card z-index-2">
          <div class="card-header p-3 pt-2">
            <div class="icon icon-lg icon-shape bg-gradient-success shadow-success text-center border-radius-xl mt-n4 me-3 float-start">
              <i class="material-icons opacity-10 graph-icon-fit">payments</i>
            </div>
            <div class="d-block">
              <p class="text-sm mb-0 text-capitalize text-secondary">{{ $t('analytics.toolsTotalPurchasePrice') || 'Tools Total Purchase Price' }}</p>
              <h4 class="mb-0 font-weight-bolder">{{ toolPruchasePriceTotal }} Kr.</h4>
            </div>
          </div>
          <div class="p-3 card-body">
            <div class="chart">
              <canvas id="purchase-price-chart" class="chart-canvas" height="250"></canvas>
            </div>
          </div>
        </div>
      </div>

      <!-- Tool Status Pie Chart -->
      <div v-if="toolStatusPieChartLoaded" class="col-lg-6 col-12 mb-4">
        <div class="card z-index-2">
          <div class="card-header p-3 pt-2">
            <div class="icon icon-lg icon-shape bg-gradient-warning shadow-warning text-center border-radius-xl mt-n4 me-3 float-start">
              <i class="material-icons opacity-10 graph-icon-fit">pie_chart</i>
            </div>
            <div class="d-block">
              <p class="text-sm mb-0 text-capitalize text-secondary">{{ $t('analytics.toolsByStatus') || 'Tools by Status' }}</p>
              <h4 class="mb-0 font-weight-bolder">{{ totalTools }} {{ $t('analytics.totalTools') || 'Total Tools' }}</h4>
            </div>
          </div>
          <div class="p-3 card-body">
            <div class="d-flex">
              <div class="chart w-50">
                <canvas id="status-pie-chart" class="chart-canvas" height="200"></canvas>
              </div>
              <div class="w-50 my-auto ps-4">
                <div v-for="(item, index) in statusTotalsLegendItems" :key="index" class="mb-2">
                  <span class="badge badge-lg badge-dot d-block text-start">
                    <i :style="{ backgroundColor: item.color }"></i>
                    <span class="text-dark text-sm">{{ item.label }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Absence Report -->
    <div class="row mt-4">
      <div class="col-12">
        <div class="card">
          <div class="card-header p-3 pt-2">
            <div class="icon icon-lg icon-shape bg-gradient-danger shadow-danger text-center border-radius-xl mt-n4 me-3 float-start">
              <i class="material-icons opacity-10 graph-icon-fit">event_busy</i>
            </div>
            <div class="d-block">
              <p class="text-sm mb-0 text-capitalize text-secondary">Fjarvistaskýrsla</p>
              <h4 class="mb-0 font-weight-bolder">Síðustu 12 mánuðir</h4>
            </div>
          </div>
          <div class="p-3 card-body">
            <div v-if="absenceReportLoading" class="text-center py-3">
              <div class="spinner-border text-secondary spinner-border-sm" role="status"></div>
            </div>
            <div v-else-if="absenceReport.length === 0" class="text-secondary text-sm">
              Engar fjarvistaskráningar fundust.
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Starfsmaður</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder text-center">Heildardagar</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder text-center">Veikindi</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder text-center">Barnaveikindi</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in absenceReport" :key="row.userId">
                    <td class="text-sm">{{ row.userName }}</td>
                    <td class="text-center text-sm font-weight-bold">{{ row.totalAbsenceDays }}</td>
                    <td class="text-center text-sm">{{ row.sicknessDays }}</td>
                    <td class="text-center text-sm">{{ row.childSicknessDays }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    </template><!-- end overview tab -->

    <!-- Time Reports Tab -->
    <template v-if="activeTab === 'timeReports' && enableTimeRegistration">
      <div v-if="timesheetLoading" class="row">
        <div class="col-12 text-center py-4">
          <div class="spinner-border text-secondary" role="status"></div>
        </div>
      </div>

      <template v-else>
        <!-- Row: Total Hours + Overtime -->
        <div class="row">
          <div class="col-lg-6 col-12 mb-4">
            <div class="card z-index-2">
              <div class="card-header p-3 pt-2">
                <div class="icon icon-lg icon-shape bg-gradient-info shadow-info text-center border-radius-xl mt-n4 me-3 float-start">
                  <i class="material-icons opacity-10 graph-icon-fit">schedule</i>
                </div>
                <div class="d-block">
                  <p class="text-sm mb-0 text-capitalize text-secondary">{{ $t('analytics.weeklyTotalHours') }}</p>
                  <h4 class="mb-0 font-weight-bolder">{{ timesheetLastWeekHours }} {{ $t('timeRegistration.hrs') }}</h4>
                </div>
              </div>
              <div class="p-3 card-body">
                <div class="chart">
                  <canvas id="ts-weekly-hours-chart" class="chart-canvas" height="250"></canvas>
                </div>
              </div>
            </div>
          </div>

          <div class="col-lg-6 col-12 mb-4">
            <div class="card z-index-2">
              <div class="card-header p-3 pt-2">
                <div class="icon icon-lg icon-shape bg-gradient-warning shadow-warning text-center border-radius-xl mt-n4 me-3 float-start">
                  <i class="material-icons opacity-10 graph-icon-fit">timer</i>
                </div>
                <div class="d-block">
                  <p class="text-sm mb-0 text-capitalize text-secondary">{{ $t('analytics.weeklyOvertimeHours') }}</p>
                  <h4 class="mb-0 font-weight-bolder">{{ timesheetLastWeekOvertime }} {{ $t('timeRegistration.hrs') }}</h4>
                </div>
              </div>
              <div class="p-3 card-body">
                <div class="chart">
                  <canvas id="ts-weekly-overtime-chart" class="chart-canvas" height="250"></canvas>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Row: Compliance -->
        <div class="row">
          <div class="col-12 mb-4">
            <div class="card z-index-2">
              <div class="card-header p-3 pt-2">
                <div class="icon icon-lg icon-shape bg-gradient-success shadow-success text-center border-radius-xl mt-n4 me-3 float-start">
                  <i class="material-icons opacity-10 graph-icon-fit">how_to_reg</i>
                </div>
                <div class="d-block">
                  <p class="text-sm mb-0 text-capitalize text-secondary">{{ $t('analytics.registrationCompliance') }}</p>
                  <h4 class="mb-0 font-weight-bolder">{{ timesheetLastWeekCompliant }} / {{ timesheetLastWeekTotal }} {{ $t('analytics.employeesRegistered') }}</h4>
                </div>
              </div>
              <div class="p-3 card-body">
                <div class="chart">
                  <canvas id="ts-compliance-chart" class="chart-canvas" height="200"></canvas>
                </div>
                <div class="d-flex gap-4 justify-content-center mt-2 text-sm">
                  <span><span class="legend-dot" style="background:#4caf50"></span> {{ $t('analytics.fullyRegistered') }}</span>
                  <span><span class="legend-dot" style="background:#f44336"></span> {{ $t('analytics.notFullyRegistered') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template><!-- end timeReports tab -->
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import api from "../../services/api";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { getCustomerConfig } from "../Settings/customerConfigHelper.js";
import ToolUtilizationReport from "./ToolUtilizationReport.vue";
import RevenueReport from "./RevenueReport.vue";
import LaborByProjectReport from "./LaborByProjectReport.vue";
import ToolDowntimeReport from "./ToolDowntimeReport.vue";
import OverdueReservationsReport from "./OverdueReservationsReport.vue";
import AbsenceReport from "./AbsenceReport.vue";
import AssetRoiReport from "./AssetRoiReport.vue";
import EndCustomerBillingReport from "./EndCustomerBillingReport.vue";

// SuperAdmin-gated report tabs: config flag -> pill metadata + inline component.
const REPORT_TABS = [
  { key: "rptToolUtilization", config: "EnableToolUtilizationReport", labelKey: "reports.toolUtilizationTitle", icon: "donut_large", component: "ToolUtilizationReport" },
  { key: "rptRevenue", config: "EnableRevenueReport", labelKey: "reports.revenueTitle", icon: "payments", component: "RevenueReport" },
  { key: "rptLaborByProject", config: "EnableLaborByProjectReport", labelKey: "reports.laborByProjectTitle", icon: "engineering", component: "LaborByProjectReport" },
  { key: "rptToolDowntime", config: "EnableToolDowntimeReport", labelKey: "reports.toolDowntimeTitle", icon: "build_circle", component: "ToolDowntimeReport" },
  { key: "rptOverdueReservations", config: "EnableOverdueReservationsReport", labelKey: "reports.overdueReservationsTitle", icon: "running_with_errors", component: "OverdueReservationsReport" },
  { key: "rptAbsence", config: "EnableAbsenceReport", labelKey: "reports.absenceTitle", icon: "event_busy", component: "AbsenceReport" },
  { key: "rptAssetRoi", config: "EnableAssetRoiReport", labelKey: "reports.assetRoiTitle", icon: "savings", component: "AssetRoiReport" },
  { key: "rptEndCustomerBilling", config: "EnableEndCustomerBillingReport", labelKey: "reports.endCustomerBillingTitle", icon: "receipt_long", component: "EndCustomerBillingReport" },
];

export default {
  name: "GeneralAnalytics",
  components: {
    ToolUtilizationReport,
    RevenueReport,
    LaborByProjectReport,
    ToolDowntimeReport,
    OverdueReservationsReport,
    AbsenceReport,
    AssetRoiReport,
    EndCustomerBillingReport,
  },
  data() {
    return {
      reportTabs: REPORT_TABS,
      enabledReports: {},
      userCount: 0,
      totalTools: 0,
      toolPruchasePriceTotal: 0,
      toolStatusData: [],
      toolStatusPieChartLoaded: false,
      toolTypePurchasePriceData: [],
      toolTypePurchasePricePieChartLoaded: false,
      projectStatusData: [],
      projectReservationBarChartLoaded: false,
      usersWithReservationCount: 0,
      userReservationStatusData: [],
      userReservationBarChartLoaded: false,
      statusCardsLoaded: false,
      statsCards: [],
      toolGroupCount: 0,
      projectWithToolCount: 0,
      toolGroupDataLoaded: false,
      usersLoaded: false,
      
      // Chart data storage
      projectChartLabels: [],
      projectChartData: [],
      userChartLabels: [],
      userChartData: [],
      purchasePriceLabels: [],
      purchasePriceData: [],
      statusLabels: [],
      statusData: [],
      statusColors: ['#344675', '#42b883', '#1d8cf8', '#fd5d93', '#ff8a76', '#00d6b4'],
      
      // Chart instances
      projectsChart: null,
      usersChart: null,
      purchasePriceChart: null,
      statusChart: null,

      // Absence report
      absenceReport: [],
      absenceReportLoading: false,

      // Nav pill tab
      activeTab: "overview",

      // Timesheet analytics (Admin/SuperAdmin + EnableTimeRegistration)
      enableTimeRegistration: false,
      timesheetLoaded: false,
      timesheetLoading: false,
      timesheetWeeklyHours: [],
      timesheetWeeklyOvertime: [],
      timesheetCompliance: [],
      tsHoursChart: null,
      tsOvertimeChart: null,
      tsComplianceChart: null,
    };
  },
  computed: {
    visibleReportTabs() {
      return this.reportTabs.filter((t) => this.enabledReports[t.config]);
    },
    activeReportComponent() {
      const tab = this.reportTabs.find((t) => t.key === this.activeTab);
      return tab ? tab.component : null;
    },
    statusTotalsLegendItems() {
      return this.statusLabels.map((label, index) => ({
        color: this.statusColors[index % this.statusColors.length],
        label: label
      }));
    },
    timesheetLastWeekHours() {
      const last = this.timesheetWeeklyHours.find((w) => w.isLastWeek);
      return last ? last.hours.toFixed(1) : "–";
    },
    timesheetLastWeekOvertime() {
      const last = this.timesheetWeeklyOvertime.find((w) => w.isLastWeek);
      return last ? last.hours.toFixed(1) : "–";
    },
    timesheetLastWeekCompliant() {
      const last = this.timesheetCompliance.find((w) => w.isLastWeek);
      return last ? last.compliant : "–";
    },
    timesheetLastWeekTotal() {
      const last = this.timesheetCompliance.find((w) => w.isLastWeek);
      return last ? last.compliant + last.nonCompliant : "–";
    },
  },
  watch: {
    toolGroupDataLoaded: 'populateStatsCards',
    userReservationBarChartLoaded: 'populateStatsCards',
    projectReservationBarChartLoaded: 'populateStatsCards',
    toolStatusPieChartLoaded: 'populateStatsCards',
    usersLoaded: 'populateStatsCards',
  },
  async created() {
    this.fetchToolGroupData();
    this.fetchToolStatus();
    this.fetchProjectStatus();
    this.fetchReservationStatus();
    this.fetchToolTypePurchasePriceTotals();
    this.fetchUsers();
    this.fetchAbsenceReport();
    this.loadReportConfigs();
    await this.checkTimesheetEnabled();
  },
  beforeUnmount() {
    if (this.projectsChart) this.projectsChart.destroy();
    if (this.usersChart) this.usersChart.destroy();
    if (this.purchasePriceChart) this.purchasePriceChart.destroy();
    if (this.statusChart) this.statusChart.destroy();
    if (this.tsHoursChart) this.tsHoursChart.destroy();
    if (this.tsOvertimeChart) this.tsOvertimeChart.destroy();
    if (this.tsComplianceChart) this.tsComplianceChart.destroy();
  },
  methods: {
    getCardIconClass(type) {
      const classMap = {
        'info': 'bg-gradient-info',
        'warning': 'bg-gradient-warning',
        'primary': 'bg-gradient-primary',
        'danger': 'bg-gradient-danger',
        'success': 'bg-gradient-success'
      };
      return classMap[type] || 'bg-gradient-dark';
    },
    getCardIcon(type) {
      const iconMap = {
        'info': 'person',
        'warning': 'list_alt',
        'primary': 'build',
        'danger': 'directions_car'
      };
      return iconMap[type] || 'analytics';
    },
    renderProjectsChart() {
      this.$nextTick(() => {
        const ctx = document.getElementById('projects-bar-chart');
        if (!ctx) return;
        
        if (this.projectsChart) {
          this.projectsChart.destroy();
        }
        
        this.projectsChart = new Chart(ctx.getContext('2d'), {
          type: 'bar',
          data: {
            labels: this.projectChartLabels,
            datasets: [{
              label: 'Tool Count',
              data: this.projectChartData,
              backgroundColor: '#f5365c',
              borderRadius: 4,
              maxBarThickness: 35
            }]
          },
          options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: {
              y: {
                grid: { display: false, drawBorder: false },
                ticks: { color: '#9ca2b7', padding: 10 }
              },
              x: {
                grid: { drawBorder: false, borderDash: [5, 5] },
                ticks: { color: '#9ca2b7', padding: 10 }
              }
            }
          }
        });
      });
    },
    renderUsersChart() {
      this.$nextTick(() => {
        const ctx = document.getElementById('users-bar-chart');
        if (!ctx) return;
        
        if (this.usersChart) {
          this.usersChart.destroy();
        }
        
        this.usersChart = new Chart(ctx.getContext('2d'), {
          type: 'bar',
          data: {
            labels: this.userChartLabels,
            datasets: [{
              label: 'Tool Count',
              data: this.userChartData,
              backgroundColor: '#11cdef',
              borderRadius: 4,
              maxBarThickness: 35
            }]
          },
          options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: {
              y: {
                grid: { display: false, drawBorder: false },
                ticks: { color: '#9ca2b7', padding: 10 }
              },
              x: {
                grid: { drawBorder: false, borderDash: [5, 5] },
                ticks: { color: '#9ca2b7', padding: 10 }
              }
            }
          }
        });
      });
    },
    renderPurchasePriceChart() {
      this.$nextTick(() => {
        const ctx = document.getElementById('purchase-price-chart');
        if (!ctx) return;
        
        if (this.purchasePriceChart) {
          this.purchasePriceChart.destroy();
        }
        
        this.purchasePriceChart = new Chart(ctx.getContext('2d'), {
          type: 'bar',
          data: {
            labels: this.purchasePriceLabels,
            datasets: [{
              label: 'Purchase Price',
              data: this.purchasePriceData,
              backgroundColor: '#2dce89',
              borderRadius: 4,
              maxBarThickness: 35
            }]
          },
          options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: {
              y: {
                grid: { display: false, drawBorder: false },
                ticks: { color: '#9ca2b7', padding: 10 }
              },
              x: {
                grid: { drawBorder: false, borderDash: [5, 5] },
                ticks: { 
                  color: '#9ca2b7', 
                  padding: 10,
                  callback: function(value) {
                    return Intl.NumberFormat('is-IS').format(value);
                  }
                }
              }
            }
          }
        });
      });
    },
    renderStatusChart() {
      this.$nextTick(() => {
        const ctx = document.getElementById('status-pie-chart');
        if (!ctx) return;
        
        if (this.statusChart) {
          this.statusChart.destroy();
        }
        
        this.statusChart = new Chart(ctx.getContext('2d'), {
          type: 'pie',
          data: {
            labels: this.statusLabels,
            datasets: [{
              data: this.statusData,
              backgroundColor: this.statusColors.slice(0, this.statusData.length),
              borderWidth: 2
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } }
          }
        });
      });
    },
    fetchToolTypePurchasePriceTotals() {
      let toolStatusUrl = "/Analytics/Tools/ToolTypePurchasePrices";
      api.get(toolStatusUrl)
        .then(response => {
          this.toolTypePurchasePriceData = response.data;
          this.toolTypePurchasePriceData.forEach(item => {
            if (item.totalPurchasePrice !== 0) {
              this.purchasePriceLabels.push(item.toolType);
              this.purchasePriceData.push(item.totalPurchasePrice);
              this.toolPruchasePriceTotal += item.totalPurchasePrice;
            }
          });
          this.toolPruchasePriceTotal = Intl.NumberFormat('is-IS').format(this.toolPruchasePriceTotal);
          this.toolTypePurchasePricePieChartLoaded = true;
          this.renderPurchasePriceChart();
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.$toast.error(this.$t('common.unableToRetrieveData'), { timeout: 0});
        });
    },
    fetchToolGroupData() {
      let toolGroupUrl = "ToolGroup?currentPage=1&pageSize=1&toolGroupTypeId=1";
      api.get(toolGroupUrl)
        .then(response => {
          this.toolGroupCount = response.data.totalItems;
          this.toolGroupDataLoaded = true;
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.$toast.error(this.$t('common.unableToRetrieveData'), { timeout: 0});
        });
    },
    fetchToolStatus() {
      let toolStatusUrl = "/Analytics/Tools/ToolStatus";
      api.get(toolStatusUrl)
        .then(response => {
          this.toolStatusData = response.data;
          this.toolStatusData.forEach(item => {
            this.statusLabels.push(this.getStatusTranslation(item.toolStatus));
            this.statusData.push(item.count);
            this.totalTools += item.count;
          });
          this.toolStatusPieChartLoaded = true;
          this.renderStatusChart();
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.$toast.error(this.$t('common.unableToRetrieveData'), { timeout: 0});
        });
    },
    fetchProjectStatus() {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      let today = `${year}-${month}-${day}`;
      let toolStatusUrl = `/Analytics/Projects/Series?fromDate=${today}&toDate=${today}&excludeZeroCounts=true`;
      api.get(toolStatusUrl)
        .then(response => {
          this.projectStatusData = response.data;
          this.projectStatusData.forEach(item => {
            if (item.data.length !== 0) {
              this.projectChartLabels.push(item.project);
              this.projectChartData.push(item.data[0].toolCount);
              this.projectWithToolCount++;
            }
          });
          this.projectReservationBarChartLoaded = true;
          this.renderProjectsChart();
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.$toast.error(this.$t('common.unableToRetrieveData'), { timeout: 0});
        });
    },
    async fetchAbsenceReport() {
      this.absenceReportLoading = true;
      try {
        const response = await api.get("/Timesheet/absence-report");
        this.absenceReport = response.data || [];
      } catch (e) {
        console.error("fetchAbsenceReport error:", e); // eslint-disable-line no-console
      } finally {
        this.absenceReportLoading = false;
      }
    },
    fetchUsers() {
      api.get("/User?includeDisabled=false&excludeImages=true")
        .then(response => {
          this.userCount = response.data.totalItems;
          this.usersLoaded = true;
        })
        .catch(error => {
          console.error('Error fetching user count:', error); // eslint-disable-line no-console
          this.$toast.error(this.$t('common.unableToRetrieveData'), { timeout: 0 });
        });
    },
    fetchReservationStatus() {
      let toolReservationsUrl = "/Analytics/Users/ToolReservations";
      api.get(toolReservationsUrl)
        .then(response => {
          this.userReservationStatusData = response.data;
          this.userReservationStatusData.forEach(item => {
            if (item.toolCount !== 0) {
              this.userChartLabels.push(item.user);
              this.userChartData.push(item.toolCount);
              this.usersWithReservationCount++;
            }
          });
          this.userReservationBarChartLoaded = true;
          this.renderUsersChart();
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.$toast.error(this.$t('common.unableToRetrieveData'), { timeout: 0});
        });
    },
    async checkTimesheetEnabled() {
      // Only show the Time Reports pill for Admin/SuperAdmin with EnableTimeRegistration configured
      try {
        const token = Cookies.get("accessToken");
        if (!token) return;
        const decoded = jwtDecode(token);
        const role = decoded.role;
        if (role !== "Admin" && role !== "SuperAdmin") return;
        const config = await getCustomerConfig("EnableTimeRegistration");
        if (!config || config === "false" || config === "") return;
        this.enableTimeRegistration = true;
      } catch (e) {
        console.error("checkTimesheetEnabled error:", e); // eslint-disable-line no-console
      }
    },
    async loadReportConfigs() {
      // Determine which report tabs the customer has enabled (off by default).
      const entries = await Promise.all(
        this.reportTabs.map(async (t) => [t.config, (await getCustomerConfig(t.config)) === true])
      );
      this.enabledReports = Object.fromEntries(entries);
    },
    onReportTab(key) {
      this.activeTab = key;
    },
    async onOverviewTab() {
      this.activeTab = "overview";
      await this.$nextTick();
      this.renderProjectsChart();
      this.renderUsersChart();
      this.renderPurchasePriceChart();
      this.renderStatusChart();
    },
    async onTimeReportsTab() {
      this.activeTab = "timeReports";
      if (!this.timesheetLoaded) {
        await this.fetchTimesheetWeeklySummary();
      } else {
        // Data already loaded but canvases were destroyed by v-if when leaving the tab — re-render
        await this.$nextTick();
        this.renderTsHoursChart();
        this.renderTsOvertimeChart();
        this.renderTsComplianceChart();
      }
    },
    async fetchTimesheetWeeklySummary() {
      this.timesheetLoading = true;
      try {
        const response = await api.get("/Analytics/Timesheet/WeeklySummary");
        this.timesheetWeeklyHours = response.data.weeklyHours || [];
        this.timesheetWeeklyOvertime = response.data.weeklyOvertime || [];
        this.timesheetCompliance = response.data.registrationCompliance || [];
        // Set loading false first so v-else renders the canvases, then await the DOM update
        this.timesheetLoading = false;
        this.timesheetLoaded = true;
        await this.$nextTick();
        this.renderTsHoursChart();
        this.renderTsOvertimeChart();
        this.renderTsComplianceChart();
      } catch (e) {
        console.error("fetchTimesheetWeeklySummary error:", e); // eslint-disable-line no-console
        this.timesheetLoading = false;
        this.timesheetLoaded = true;
      }
    },
    renderTsHoursChart() {
      const ctx = document.getElementById("ts-weekly-hours-chart");
      if (!ctx) return;
      if (this.tsHoursChart) this.tsHoursChart.destroy();
      const labels = this.timesheetWeeklyHours.map((w) => w.weekLabel);
      const data = this.timesheetWeeklyHours.map((w) => w.hours);
      const colors = this.timesheetWeeklyHours.map((w) => w.isLastWeek ? "#1a73e8" : "#90caf9");
      this.tsHoursChart = new Chart(ctx, {
        type: "bar",
        data: { labels, datasets: [{ label: this.$t("analytics.weeklyTotalHours"), data, backgroundColor: colors, borderRadius: 4 }] },
        options: {
          responsive: true,
          plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `${c.parsed.y.toFixed(1)} ${this.$t("timeRegistration.hrs")}` } } },
          scales: { y: { beginAtZero: true, title: { display: true, text: this.$t("timeRegistration.hrs") } } },
        },
      });
    },
    renderTsOvertimeChart() {
      const ctx = document.getElementById("ts-weekly-overtime-chart");
      if (!ctx) return;
      if (this.tsOvertimeChart) this.tsOvertimeChart.destroy();
      const labels = this.timesheetWeeklyOvertime.map((w) => w.weekLabel);
      const data = this.timesheetWeeklyOvertime.map((w) => w.hours);
      const colors = this.timesheetWeeklyOvertime.map((w) => w.isLastWeek ? "#f57c00" : "#ffcc80");
      this.tsOvertimeChart = new Chart(ctx, {
        type: "bar",
        data: { labels, datasets: [{ label: this.$t("analytics.weeklyOvertimeHours"), data, backgroundColor: colors, borderRadius: 4 }] },
        options: {
          responsive: true,
          plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `${c.parsed.y.toFixed(1)} ${this.$t("timeRegistration.hrs")}` } } },
          scales: { y: { beginAtZero: true, title: { display: true, text: this.$t("timeRegistration.hrs") } } },
        },
      });
    },
    renderTsComplianceChart() {
      const ctx = document.getElementById("ts-compliance-chart");
      if (!ctx) return;
      if (this.tsComplianceChart) this.tsComplianceChart.destroy();
      const labels = this.timesheetCompliance.map((w) => w.weekLabel);
      const compliant = this.timesheetCompliance.map((w) => w.compliant);
      const nonCompliant = this.timesheetCompliance.map((w) => w.nonCompliant);
      const compliantColors = this.timesheetCompliance.map((w) => w.isLastWeek ? "#2e7d32" : "#4caf50");
      const nonCompliantColors = this.timesheetCompliance.map((w) => w.isLastWeek ? "#c62828" : "#f44336");
      this.tsComplianceChart = new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets: [
            { label: this.$t("analytics.fullyRegistered"), data: compliant, backgroundColor: compliantColors, borderRadius: 4, stack: "c" },
            { label: this.$t("analytics.notFullyRegistered"), data: nonCompliant, backgroundColor: nonCompliantColors, borderRadius: 4, stack: "c" },
          ],
        },
        options: {
          responsive: true,
          plugins: { legend: { display: false } },
          scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true, ticks: { stepSize: 1 }, title: { display: true, text: this.$t("analytics.employees") } } },
        },
      });
    },
    populateStatsCards() {
      if (this.userReservationBarChartLoaded && this.projectReservationBarChartLoaded && this.toolStatusPieChartLoaded && this.toolGroupDataLoaded && this.usersLoaded) {
        this.statsCards = [
          {
            title: `${this.userCount}`,
            subTitle: this.$t('analytics.totalActiveUsers') || 'Total Active Users',
            type: 'info',
            routingType: 'Users'
          },
          {
            title: `${this.projectStatusData.length}`,
            subTitle: this.$t('analytics.totalProjects') || 'Total Projects',
            type: 'warning',
            routingType: 'Projects'
          },
          {
            title: `${this.totalTools}`,
            subTitle: this.$t('analytics.totalTools') || 'Total Tools',
            type: 'primary',
            routingType: 'Tools'
          },
          {
            title: `${this.toolGroupCount}`,
            subTitle: this.$t('analytics.totalCars') || 'Total Cars',
            type: 'danger',
            routingType: 'Cars'
          },
        ];
        this.statusCardsLoaded = true;
      }
    },
    getStatusTranslation(status) {
      switch (status) {
        case "Free": return this.$t('toolStatus.free') || 'Free';
        case "In Use": return this.$t('toolStatus.inUse') || 'In Use';
        case "Broken": return this.$t('toolStatus.broken') || 'Broken';
        case "Out of Use": return this.$t('toolStatus.outOfUse') || 'Out of Use';
        case "Loaned": return this.$t('toolStatus.loaned') || 'Loaned';
        case "Rented": return this.$t('toolStatus.rented') || 'Rented';
        default: return 'N/A';
      }
    },
    goToRoute(card) {
      this.$router.push({ name: card.routingType });
    }
  },
};
</script>

<style scoped>
/* Icon styling */
.icon-lg {
  width: 64px;
  height: 64px;
}

.icon-shape {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
}

.icon-shape i {
  font-size: 1.5rem;
  color: #fff;
}

/* Card styling */
.card {
  border-radius: 1rem;
}

.z-index-2 {
  z-index: 2;
}

.mt-n4 {
  margin-top: -1.5rem !important;
}

/* Chart canvas */
.chart {
  position: relative;
  height: 100%;
}

.chart-canvas {
  height: 100% !important;
}

/* Cursor pointer for clickable elements */
.cursor-pointer {
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.cursor-pointer:hover {
  opacity: 0.8;
}

/* Badge dot for legend */
.badge-dot i {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
}

/* Stats card icon shadows */
.shadow-primary {
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(233, 30, 99, 0.4) !important;
}

.shadow-info {
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(0, 188, 212, 0.4) !important;
}

.shadow-success {
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(76, 175, 80, 0.4) !important;
}

.shadow-warning {
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(255, 152, 0, 0.4) !important;
}

.shadow-danger {
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(244, 67, 54, 0.4) !important;
}

.stats-icon-fit {
  height: 40px;
  scale: 1.5;
}

.graph-icon-fit {
  height: 40px;
  scale: 2;
}

.legend-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 4px;
  vertical-align: middle;
}

.analytics-pills .nav-link {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.45rem 0.9rem;
  border-radius: 2rem;
  font-size: 0.85rem;
  font-weight: 500;
  color: #6c757d;
  border: 1px solid transparent;
  transition: background 0.15s, color 0.15s;
}

.analytics-pills .nav-link:hover {
  background: #f0f2f5;
  color: #344767;
}

.analytics-pills .nav-link.active {
  background: #344767;
  color: #fff;
}
</style>
